import React, { FunctionComponent, useCallback, useEffect, useState, useMemo } from 'react'
import { components } from 'react-select'
import styled from 'styled-components'
import debounce from 'lodash.debounce'
import Row from '../components/Row'
import Text from '../components/Text'
import Column from '../components/Column'
import { IConnection } from '../interfaces/models/connection'
import AsyncSelect from '../components/select/AsyncSelect'
import ProfilePhoto from '../components/ProfilePhoto'
import SvgFragment from './SvgFragment'
import { useConnectionsRequest } from '../services/apis/connections/useConnectionsRequest'
import { styles } from '../styles/themes/style'
import { useConnectionsHandlers } from '../services/actions/connections/useConnectionsHandlers'
import { useExternalHandlers } from '../services/actions/navigation/useExternalHandlers'
import { useAppHandlers } from '../services/actions/app/useAppHandlers'
import { useAuthStore } from '../stores/auth/useAuthStore'
import { useAppStore } from '../stores/application/useAppStore'
import { isLNFirst } from '../utils'
import { SidebarTypes } from '../interfaces/store/appStore'
import { useTheme } from 'styled-components'

interface IConnectionAutocompleteSelectProps {
    prefilledValue?: IConnection,
    showOnFocus?: boolean,
    showValue?: boolean,
    width?: string,
    onChangeHandler?: (e: any) => void
}

interface IEditConnection extends IConnection{
    label: string,
    value: string
}

const MemoizedProfilePhoto = React.memo(ProfilePhoto, (prevProps, nextProps) => {
    return prevProps.source === nextProps.source;
});

const OptionComponent = React.memo((itemProps: any) => {
    const profilePicture = itemProps.data?.profilePicture;
    const firstName = itemProps.data?.firstName;
    const lastName = itemProps.data?.lastName;
    const email = itemProps.data?.email;
    
    return (
        <StyledOption>
            <Row gap="6px" alignItems {...itemProps.innerProps}>
                <MemoizedProfilePhoto source={profilePicture} />
                <Column>
                    <Text $label>{`${firstName} ${lastName}`}</Text>
                    <Text $lighter>{email}</Text>
                </Column>
            </Row>
        </StyledOption>
    )
}, (prevProps, nextProps) => {
    // Re-render only if data actually changed
    return prevProps.data?._id === nextProps.data?._id &&
           prevProps.data?.profilePicture === nextProps.data?.profilePicture &&
           prevProps.data?.firstName === nextProps.data?.firstName &&
           prevProps.data?.lastName === nextProps.data?.lastName &&
           prevProps.data?.email === nextProps.data?.email &&
           prevProps.isSelected === nextProps.isSelected &&
           prevProps.isFocused === nextProps.isFocused;
});

const SingleValueComponent: FunctionComponent<any> = (itemProps: any) => {
    const { messageClickHandler } = useConnectionsHandlers();
    const { openSidebar } = useAppHandlers();
    const { store: { user, isLoggedToLinkedin, workspace } } = useAuthStore();
    const { store: { extensionInstalled, extensionVersion, chatPopups } } = useAppStore();
    const { visitLIProfile } = useExternalHandlers();
    const [loadingConversation, setLoadingConversation] = useState<string | null>(null);
    
    if (!itemProps.data?.value) return null;
    
    const connection = itemProps.data as IConnection;
    const profilePicture = connection?.profilePicture;
    const firstName = connection?.firstName;
    const lastName = connection?.lastName;
    const fullName = `${firstName} ${lastName}`;
    
    const isLinkedinFirst = isLNFirst(connection, user?._id);
    const canMessage = workspace?.isStarter === false && 
                       isLinkedinFirst && 
                       extensionInstalled && 
                       isLoggedToLinkedin && 
                       extensionVersion === process.env.REACT_APP_VERSION;
    
    const handleNameClick = (e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault();
        e.stopPropagation();
        // Extract the actual connection object from associations.connections[0]
        const actualConnection = (connection as any)?.associations?.connections?.[0] || connection;
        if (actualConnection) {
            openSidebar(SidebarTypes.CONNECTION_ACTIONS, { connection: actualConnection });
        }
    };
    
    const handleMessageClick = async (e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (canMessage && connection) {
            await messageClickHandler(e, connection, setLoadingConversation);
        }
    };
    
    const handleConnectClick = (e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (connection?.publicIdentifier) {
            visitLIProfile(connection.publicIdentifier);
        }
    };
    
    return (
        <SelectedConnectionWrapper>
            <ProfilePhotoWrapper>
                <MemoizedProfilePhoto source={profilePicture} height="24px" width="24px" />
            </ProfilePhotoWrapper>
            <NameAndButtonWrapper>
                <NameLink data-ld-clickable="true" onMouseDown={handleNameClick}>
                    <Text $label $ellipsis="1" style={{ color: styles.colors.black600 }}>{fullName}</Text>
                </NameLink>
                {canMessage ? (
                    <MessageButton 
                        data-ld-clickable="true"
                        onMouseDown={handleMessageClick}
                        disabled={loadingConversation === connection._id || chatPopups.length >= 4}
                    >
                        {loadingConversation === connection._id ? 'Loading...' : 'Message'}
                    </MessageButton>
                ) : connection?.publicIdentifier ? (
                    <ConnectButton data-ld-clickable="true" onMouseDown={handleConnectClick}>
                        Connect
                    </ConnectButton>
                ) : null}
            </NameAndButtonWrapper>
        </SelectedConnectionWrapper>
    )
};

const ConnectionAutocompleteSelect: FunctionComponent<IConnectionAutocompleteSelectProps> = ({ prefilledValue, showOnFocus, width, showValue=true, onChangeHandler }) => {
    const { getConnections } = useConnectionsRequest();
    const [ value, setValue ] = useState<IEditConnection | undefined>(undefined)
    const theme = useTheme();

    useEffect(() => {
        if (prefilledValue) {
            setValue({
                ...prefilledValue,
                label: `${prefilledValue.firstName} ${prefilledValue.lastName}`,
                value: prefilledValue._id
            })
        }
    }, [prefilledValue])

    const onChange = useCallback((option: IEditConnection) => {
        if(option) {
            const {label, value: newValue, ...newConnection} = option
            onChangeHandler && onChangeHandler(newConnection)
            // If showValue is false, reset value to undefined and blur to show placeholder again
            if (!showValue) {
                setValue(undefined)
                setIsFocused(false)
                // Blur the input element to remove focus
                setTimeout(() => {
                    const selectContainer = document.querySelector('[class*="react-select"]');
                    const inputElement = selectContainer?.querySelector('input[type="text"]') as HTMLInputElement;
                    if (inputElement) {
                        inputElement.blur();
                    }
                }, 0);
            } else {
                setValue({...option })
            }
        } else {
            setValue(undefined)
        }
    }, [onChangeHandler, showValue])

    const [isFocused, setIsFocused] = useState(false);

    const handleFocus = useCallback(() => {
        setIsFocused(true);
    }, []);

    const handleBlur = useCallback(() => {
        setIsFocused(false);
    }, []);

    const loadOptionsHandler = useMemo(
        () => debounce(async (inputValue: string, callback: any) => {
            if(inputValue.trim())
            {
                try{
                    let connectionData = await getConnections({page: 1, pageSize: 5, searchValues: encodeURIComponent(JSON.stringify([{searchByName: 'yes', searchValue: inputValue}]))  });
                    //todo: filter for everything but forbidden

                    callback(connectionData?.connections?.map((connection: IConnection) => ({
                        ...connection,
                        label: `${connection.firstName} ${connection.lastName}`,
                        value: connection._id
                    })))
                }
                catch(error){
                    callback([])
                }
            }    
        }, 500),
        [getConnections]
    );

    const PlaceholderComponent = useCallback((props: any) => {
        // Hide placeholder when the field is focused
        if (isFocused) {
            return null;
        }
        return <components.Placeholder {...props} />;
    }, [isFocused]);

    const ClearIndicatorComponent = useCallback((props: any) => {
        return (
            <components.ClearIndicator
                {...props}
                innerProps={{
                    ...props.innerProps,
                    onMouseDown: (e: React.MouseEvent) => {
                        e.preventDefault();
                        e.stopPropagation();
                        // Call original clear handler
                        if (props.clearValue) {
                            props.clearValue();
                        }
                        // Set cursor after value is cleared
                        setTimeout(() => {
                            const selectContainer = document.querySelector('[class*="react-select"]');
                            const inputElement = selectContainer?.querySelector('input[type="text"]') as HTMLInputElement;
                            if (inputElement) {
                                inputElement.focus();
                                inputElement.setSelectionRange(0, 0);
                            }
                        }, 0);
                    }
                }}
            >
                <StyledClearIndicator data-ld-clickable="true">
                    <SvgFragment type="dropdownClose" />
                </StyledClearIndicator>
            </components.ClearIndicator>
        );
    }, []);

    const ControlComponent = useCallback((controlProps: any) => {
        const originalOnMouseDown = controlProps?.innerProps?.onMouseDown

        const onMouseDown = (e: React.MouseEvent) => {
            if (value) {
                const target = e.target as HTMLElement
                const isAllowedClickable =
                    !!target.closest('[data-ld-clickable="true"]') ||
                    !!target.closest('[class*="indicatorsContainer"]')

                // When a contact is selected, prevent react-select from opening/focusing unless the user clicks
                // on explicitly clickable elements (name, buttons, clear indicator).
                if (!isAllowedClickable) {
                    e.preventDefault()
                    e.stopPropagation()
                    return
                }
            }

            originalOnMouseDown?.(e)
        }

        return (
            <components.Control
                {...controlProps}
                innerProps={{
                    ...controlProps.innerProps,
                    onMouseDown
                }}
            />
        )
    }, [value])

    const selectComponents = useMemo(() => ({
        Placeholder: PlaceholderComponent,
        DropdownIndicator: () => null,
        ClearIndicator: ClearIndicatorComponent,
        Option: OptionComponent,
        SingleValue: SingleValueComponent,
        Control: ControlComponent,
    }), [PlaceholderComponent, ClearIndicatorComponent, ControlComponent]);

    return  <AsyncSelect 
        containerHeight="34px"
        isSearchable={!value}
        isClearable
        openMenuOnClick={!value}
        openMenuOnFocus={!value}
        placeholder = {'Type contact name...'}
        maxLength={30}
        onChangeHandler = {(e: any) => onChange(e)}
        value = {showValue ? value : null}
        showOnFocus={showOnFocus}
        width={width}
        loadOptions = { loadOptionsHandler }
        onFocus={handleFocus}
        onBlur={handleBlur}
        components={selectComponents}
        menuIsOpen={value ? false : undefined}
        styles={{
            control: (base: any, props: any) => ({
                ...base,
                minHeight: '34px',
                height: '34px',
                backgroundColor: value ? styles.colors.black100 : base.backgroundColor,
                cursor: props.selectProps.isDisabled ? 'default' : 'pointer',
                border: `2px solid ${theme.quinaryColor}`,
                boxShadow: 'none',
                '&:hover': {
                    boxShadow: 'none',
                    border: `2px solid ${props.selectProps.isDisabled ? styles.colors.black200 : styles.colors.primary600}`,
                    cursor: props.selectProps.isDisabled ? 'default' : 'pointer',
                },
                '&:active': {
                    boxShadow: 'none',
                    border: `2px solid ${props.selectProps.isDisabled ? styles.colors.black200 : styles.colors.primary600}`,
                }
            }),
            valueContainer: (base: any) => ({
                ...base,
                padding: '0 10px',
                height: '34px',
                display: 'flex',
                alignItems: 'center',
                cursor: 'default',
            }),
            indicatorsContainer: (base: any) => ({
                ...base,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '30px',
                marginRight: '4px',
            }),
            clearIndicator: (base: any) => ({
                ...base,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0px',
                cursor: 'pointer',
            })
        }}
    />
}

const StyledOption = styled.div`
    padding: 6px 10px;
    cursor: pointer;
`

const StyledClearIndicator = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    
    &:hover svg path {
        fill: ${styles.colors.primary600};
    }
`

const SelectedConnectionWrapper = styled(Row)`
    width: 100%;
    align-items: center;
    gap: 6px;
    background-color: transparent;
    min-width: 0;
    height: 92%;
`

const NameAndButtonWrapper = styled(Row)`
    flex: 1;
    min-width: 0;
    align-items: center;
    gap: 8px;
`

const ProfilePhotoWrapper = styled.div`
    flex-shrink: 0;
`

const NameLink = styled.div`
    cursor: pointer;
    position: relative;
    z-index: 10;
    min-width: 0;

    overflow: hidden;
    
    span {
        color: ${styles.colors.black600};
        text-decoration: none;
    }
    
    &:hover span {
        text-decoration: underline;
    }
`

const MessageButton = styled.button`
    background: none;
    border: none;
    color: ${styles.colors.primary600};
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    padding: 0;
    margin-left: auto;
    position: relative;
    z-index: 10;
    flex-shrink: 0;
    white-space: nowrap;
    
    &:hover {
        text-decoration: underline;
    }
    
    &:disabled {
        cursor: not-allowed;
        opacity: 0.6;
    }
`

const ConnectButton = styled.button`
    background: none;
    border: none;
    color: ${styles.colors.primary600};
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    padding: 0;
    margin-left: auto;
    position: relative;
    z-index: 10;
    flex-shrink: 0;
    white-space: nowrap;
    
    &:hover {
        text-decoration: underline;
    }
`

export default ConnectionAutocompleteSelect
